/*
  Two custom elements:

  <mega-menu-item>  opens a top-level mega menu on hover / focus, closes on
                    mouseleave, Escape or focus moving out of the header.
  <mega-panel>      swaps the level-3 links and promo images when a level-2
                    item is hovered or focused. Index 0 is active on open.
*/

const MEGA_OPEN_DELAY = 100;
const MEGA_CLOSE_DELAY = 200;
const PANEL_SWITCH_DELAY = 60;

class MegaMenuItem extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-mega-trigger]');
    this.content = this.querySelector('.mega-menu__content');
    this.openTimer = null;
    this.closeTimer = null;

    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);

    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeydown);
    this.trigger?.addEventListener('click', this.onTriggerClick);
  }

  disconnectedCallback() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.removeEventListener('mouseenter', this.onEnter);
    this.removeEventListener('mouseleave', this.onLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeydown);
    this.trigger?.removeEventListener('click', this.onTriggerClick);
  }

  get isTouch() {
    return window.matchMedia('(hover: none)').matches;
  }

  onEnter() {
    if (this.isTouch) return;
    clearTimeout(this.closeTimer);
    this.openTimer = setTimeout(() => this.open(), MEGA_OPEN_DELAY);
  }

  onLeave() {
    if (this.isTouch) return;
    clearTimeout(this.openTimer);
    this.closeTimer = setTimeout(() => this.close(), MEGA_CLOSE_DELAY);
  }

  onFocusIn() {
    clearTimeout(this.closeTimer);
    this.open();
  }

  onFocusOut(event) {
    if (this.contains(event.relatedTarget)) return;
    this.close();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    this.close();
    this.trigger?.focus();
  }

  // On touch there is no hover, so the first tap opens the panel and the
  // second tap follows the link.
  onTriggerClick(event) {
    if (!this.isTouch) return;
    if (this.hasAttribute('open')) return;
    event.preventDefault();
    this.open();
  }

  open() {
    if (this.hasAttribute('open')) return;

    document.querySelectorAll('mega-menu-item[open]').forEach((item) => {
      if (item !== this) item.close();
    });

    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');
    // reset() is missing until the custom element upgrades, so call it defensively
    this.querySelector('mega-panel')?.reset?.();
  }

  close() {
    clearTimeout(this.openTimer);
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }
}

class MegaPanel extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-panel-index]'));
    this.groups = Array.from(this.querySelectorAll('[data-panel-group]'));
    this.switchTimer = null;

    this.onPointerOver = this.onPointerOver.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', this.onPointerOver);
      trigger.addEventListener('focus', this.onFocusIn);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.switchTimer);
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('mouseenter', this.onPointerOver);
      trigger.removeEventListener('focus', this.onFocusIn);
    });
  }

  onPointerOver(event) {
    clearTimeout(this.switchTimer);
    const index = event.currentTarget.dataset.panelIndex;
    // Small delay so sweeping the cursor across the list doesn't thrash the panel
    this.switchTimer = setTimeout(() => this.activate(index), PANEL_SWITCH_DELAY);
  }

  onFocusIn(event) {
    clearTimeout(this.switchTimer);
    this.activate(event.currentTarget.dataset.panelIndex);
  }

  activate(index) {
    this.triggers.forEach((trigger) => {
      trigger.toggleAttribute('data-panel-active', trigger.dataset.panelIndex === index);
    });

    this.groups.forEach((group) => {
      group.toggleAttribute('data-panel-active', group.dataset.panelGroup === index);
    });
  }

  reset() {
    clearTimeout(this.switchTimer);
    this.activate('0');
  }
}

if (!customElements.get('mega-menu-item')) {
  customElements.define('mega-menu-item', MegaMenuItem);
}

if (!customElements.get('mega-panel')) {
  customElements.define('mega-panel', MegaPanel);
}
